import { CONFIG, getBoundFetch, getQueryClient } from "@/modules/core";
import { InfiniteData, useMutation } from "@tanstack/react-query";
import { Fragment } from "../types";
import { getFragmentsQueryOptions } from "../queries";
import { buildAddedFragment } from "../utils/fragment-cache-helpers";
import { WrappedResponse } from "@/modules/core/types";

export function useAddFragment(username: string, code: string | undefined) {
  return useMutation({
    mutationKey: ["posts", "add-fragment", username],
    mutationFn: async ({ title, body }: { title: string; body: string }) => {
      if (!code) {
        throw new Error("[SDK][Posts] Missing access token");
      }
      const fetchApi = getBoundFetch();
      const response = await fetchApi(
        CONFIG.privateApiHost + "/private-api/fragments-add",
        {
          method: "POST",
          body: JSON.stringify({
            code,
            title,
            body,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      // Raw fetch resolves on 4xx/5xx, so a JSON error response would otherwise run
      // onSuccess and insert a bogus fragment into the cache. Throw instead.
      if (!response.ok) {
        throw new Error(`[SDK][Posts] Failed to add fragment: ${response.status}`);
      }
      return response.json() as Promise<Fragment>;
    },
    onSuccess(response, variables) {
      const queryClient = getQueryClient();

      // Reaffirm the submitted title/body over the server acknowledgement so a
      // freshly added snippet never renders blank if the response omits them,
      // while keeping any server-provided id/timestamps.
      const newFragment = buildAddedFragment(response, variables);

      // Update regular query cache
      queryClient.setQueryData<Fragment[]>(
        getFragmentsQueryOptions(username, code).queryKey,
        (data) => [newFragment, ...(data ?? [])]
      );

      // Update infinite query cache - add new fragment to first page
      queryClient.setQueriesData<InfiniteData<WrappedResponse<Fragment>>>(
        { queryKey: ["posts", "fragments", "infinite", username] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page, index) =>
              index === 0
                ? { ...page, data: [newFragment, ...page.data] }
                : page
            ),
          };
        }
      );
    },
  });
}
